import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/types/database";

/**
 * SPEC 6.3 (revised per REVIEW finding #4):
 *  - Magic-link only at first sign-in.
 *  - JWT 90d, refresh 365d, auto-refresh — feels like "once per device".
 *  - Session persisted to localStorage (default for @supabase/supabase-js on web).
 *
 * Frontend only ever uses anon key — RLS protects data. Service-role key lives in Worker only.
 *
 * Database types are generated from the remote schema by `supabase gen types
 * typescript --linked` (see scripts/gen-types.sh). Regenerate after every migration.
 */

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  // Boot-loud: missing env is a fatal config error, not a runtime fallback.
  // eslint-disable-next-line no-console
  console.warn(
    "[supabase] VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY missing — auth + queries will fail until set in .env.local.",
  );
}

export const supabase = createClient<Database>(url ?? "", anonKey ?? "", {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: typeof window !== "undefined" ? window.localStorage : undefined,
    storageKey: "haus.supabase.auth",
    flowType: "pkce",
  },
  global: {
    headers: { "x-application-name": "haus-web" },
  },
});
